import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CloudOff, Hourglass, KeyRound, Settings, TriangleAlert } from 'lucide-react';
import { Button } from '../ui';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Estados honestos da IA (2f), sem nomes de variáveis para quem não é super admin.
 * kind: NOT_CONFIGURED | DAILY_LIMIT | MONTHLY_CAP | ERROR | DEMO
 */
export function AIState({ kind, status, message, onRetry, compact = false }) {
  const { isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const ownKey = status?.userKeysAllowed && !status?.hasUserKey && status?.canUseUserKeyAsFallback;

  if (kind === 'NOT_CONFIGURED' && isSuperAdmin) {
    return (
      <div className={`ai-state ai-state--setup ${compact ? 'is-compact' : ''}`} role="status">
        <span className="ai-state__icon"><Settings size={18} aria-hidden="true" /></span>
        <span className="ai-state__text">
          <strong>Configure um provedor de IA</strong>
          <span>Nenhuma chave encontrada (ANTHROPIC_API_KEY, OPENAI_API_KEY ou OLLAMA_BASE_URL).</span>
        </span>
        <Button size="sm" variant="outline" onClick={() => navigate('/admin/ia')}>Ver como</Button>
      </div>
    );
  }
  if (kind === 'NOT_CONFIGURED' || kind === 'DEMO' || kind === 'MONTHLY_CAP') {
    return (
      <div className={`ai-state ai-state--muted ${compact ? 'is-compact' : ''}`} role="status">
        <span className="ai-state__icon"><CloudOff size={18} aria-hidden="true" /></span>
        <span className="ai-state__text">
          <strong>{kind === 'DEMO' ? 'O tutor fica desligado na demonstração' : 'O tutor está indisponível'}</strong>
          <span>{kind === 'DEMO' ? 'Entre com a sua conta para conversar com a IA.' : 'O restante da aula funciona normal. Avisamos quando voltar.'}</span>
        </span>
        {ownKey && <Button size="sm" variant="outline" icon={KeyRound} onClick={() => navigate('/conta?aba=ia')}>Usar minha chave</Button>}
      </div>
    );
  }
  if (kind === 'DAILY_LIMIT') {
    return (
      <div className={`ai-state ai-state--muted ${compact ? 'is-compact' : ''}`} role="status">
        <span className="ai-state__icon"><Hourglass size={18} aria-hidden="true" /></span>
        <span className="ai-state__text">
          <strong>{message || `Você usou as ${status?.messagesLimit ?? ''} mensagens de hoje`}</strong>
          <span>Renova amanhã às 00h. Flashcards e simulados continuam liberados.</span>
        </span>
        {ownKey && <Button size="sm" variant="outline" icon={KeyRound} onClick={() => navigate('/conta?aba=ia')}>Usar minha chave</Button>}
      </div>
    );
  }
  return (
    <div className={`ai-state ai-state--error ${compact ? 'is-compact' : ''}`} role="alert">
      <span className="ai-state__icon"><TriangleAlert size={18} aria-hidden="true" /></span>
      <span className="ai-state__text">
        <strong>Não consegui responder agora</strong>
        <span>{message || 'A conexão com a IA falhou.'}{onRetry ? ' Sua pergunta ficou salva.' : ''}</span>
      </span>
      {onRetry && <Button size="sm" variant="outline" onClick={onRetry}>Tentar de novo</Button>}
    </div>
  );
}

/** Converte o código de erro da API no estado da tela. */
export function stateOfError(code) {
  if (code === 'AI_NOT_CONFIGURED') return 'NOT_CONFIGURED';
  if (code === 'AI_DAILY_LIMIT') return 'DAILY_LIMIT';
  if (code === 'AI_MONTHLY_CAP') return 'MONTHLY_CAP';
  if (code === 'DEMO_RESTRICTED') return 'DEMO';
  return 'ERROR';
}

/** Estado pelo /ai/status, ou null quando a IA está pronta. */
export function blockedState(status) {
  if (!status) return null;
  if (status.state === 'DEMO') return 'DEMO';
  if (status.available) return null;
  return status.state === 'DAILY_LIMIT' || status.state === 'MONTHLY_CAP' || status.state === 'NOT_CONFIGURED' ? status.state : 'ERROR';
}
